<template>
    <loading-indicator v-if="loading" text="Optionen werden abgerufen."/>
    <div v-else>
        Der Abteilung {{assignedTaskCount === 1 ? 'ist noch eine Aufgabe' : 'sind noch ' + assignedTaskCount + ' Aufgaben'}} zugewiesen.
        Um fortzufahren, wählen Sie bitte <u>{{changeSelectLabel}}</u> aus, {{changeToLabel}} anschließend
        <u>alle Aufgaben</u> der zu löschenden Abteilung automatisch zugewiesen werden.
        <br>
        <br>

        <form-row v-if="userBasedTaskModule" class="spacer-15" label="Alternativen Nutzer auswählen">
            <form-select-user v-model="assignTaskData.owner" :options="assignTaskOptions.owners"/>
        </form-row>
        <div v-else class="spacer-15">
            <form-row :class="{disabled: assignTaskData.entity_id}" label="Alternativen Mitarbeiter auswählen">
                <form-select-employee v-model="assignTaskData.owner" :options="assignTaskOptions.owners" @input="changeOwnerAssignment"/>
            </form-row>

            <form-row v-if="entityOptions.length" :class="{disabled: assignTaskData.owner}" label="Alternative Abteilung auswählen">
                <form-select v-model="assignTaskData.entity_id" :options="entityOptions" @input="changeEntityAssignment"/>
            </form-row>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import axios from 'axios';
    import FormSelectEmployee from '@/components/form/FormSelectEmployee.vue';
    import FormSelectUser from '@/components/form/FormSelectUser.vue';
    import Loader from '@/library/intranet/loader';
    import {popupDefaultCancelButton} from '@/library/popup';
    import {errorHandler, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import type {TinyEmitter} from 'tiny-emitter';
    import {inject} from 'vue-demi';
    import FormRow from '../../../components/form/FormRow.vue';
    import FormSelect from '../../../components/form/FormSelect.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import TopMessageHandler from '../../../library/topMessageHandler';
    import {ref, computed, watch} from 'vue';

    const portuna      = usePortuna();
    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    interface AssignTaskData
    {
        owner: number;
        entity_id: number;
    }

    const emit = defineEmits<{
        'update:modelValue': [newValue: AssignTaskData];
        saved: [...args: unknown[]];
    }>();

    const props               = withDefaults(
        defineProps<{
            excludeEntityId?: number;
            assignedTaskCount: number;
            entityId: number;
            asPopup: boolean;
        }>(),
        {
            excludeEntityId: 0,
            asPopup: false,
        },
    );
    const userBasedTaskModule = ref<boolean>(true);
    const assignTaskData      = ref<AssignTaskData>({
        owner: 0,
        entity_id: 0,
    });
    const loading             = ref(true);
    const assignTaskOptions   = ref(
        {} as {
            owners: SelectOption[];
            entities: SelectOption[];
        },
    );

    const entityOptions = computed((): SelectOption[] =>
    {
        return (assignTaskOptions.value.entities || []).filter(({value}) => value !== props.excludeEntityId);
    });

    const changeSelectLabel = computed((): string =>
    {
        if (userBasedTaskModule.value)
        {
            return 'einen Nutzer';
        }
        return 'einen Mitarbeiter oder eine andere Abteilung';
    });

    const changeToLabel = computed((): string =>
    {
        if (userBasedTaskModule.value)
        {
            return 'dem';
        }
        return 'der';
    });

    const watchAssignTaskData = (newValue: AssignTaskData) =>
    {
        if (props.asPopup)
        {
            return;
        }
        emit('update:modelValue', newValue);
    };

    const fetchTaskAssignOptions = () =>
    {
        loading.value = true;
        axios
        .get('/task/entry/fetch-assign-options')
        .then((response) =>
        {
            if (response.data.status !== XHR_STATUS_DEFAULT)
            {
                throw response;
            }
            const {
                      data: {data},
                  }                   = response;
            userBasedTaskModule.value = data.userBasedTaskModule;
            assignTaskOptions.value   = data.selects;
            if (props.asPopup)
            {
                popupEmitter.emit('setButtons', [
                    popupDefaultCancelButton,
                    {
                        title: 'Abteilung deaktivieren',
                        icon: 'remove_circle',
                        type: 'danger',
                        cmd: () =>
                        {
                            Loader.show('Abteilung wird deaktiviert...');
                            axios
                            .post('/employee/entity/change-status/id/' + props.entityId, assignTaskData.value)
                            .then((response) =>
                            {
                                const {
                                          status,
                                          message,
                                      } = response.data;
                                if (status !== XHR_STATUS_DEFAULT)
                                {
                                    throw response;
                                }
                                TopMessageHandler.success(message);
                                popupEmitter.emit('saved');
                                popupEmitter.emit('close');
                            })
                            .catch(errorHandler)
                            .finally(() =>
                            {
                                Loader.hide();
                            });
                        },
                    },
                ]);
            }
        })
        .catch(errorHandler)
        .finally(() =>
        {
            loading.value = false;
        });
    };

    const changeOwnerAssignment = (newEmployee: number) =>
    {
        if (newEmployee)
        {
            assignTaskData.value.entity_id && TopMessageHandler.info('Die zugewiesene Abteilung wurde entfernt.');
            assignTaskData.value['entity_id'] = 0;
        }
    };

    const changeEntityAssignment = (newEntity: number) =>
    {
        if (newEntity)
        {
            assignTaskData.value.owner && TopMessageHandler.info('Der zugewiesene Mitarbeiter wurde entfernt.');
            assignTaskData.value['owner'] = 0;
        }
    };

    fetchTaskAssignOptions();

    watch(assignTaskData, watchAssignTaskData, {deep: true});

    defineExpose(Object.assign({}, portuna));
</script>